import { useCallback, useMemo } from 'react'
import { type ApiSearchableSelectOption } from '../components/ApiSearchableSelect'
import { usePropertyDefinitions, type PropertyDefinition } from './useQueries'
import { useApi } from '../api/hooks'

export function usePropertyDefinitionSearch(org: string) {
    const { request } = useApi()
    const { data: propertyDefinitionsAll = [] } = usePropertyDefinitions(org)

    const initialOptions: ApiSearchableSelectOption[] = useMemo(() =>
        propertyDefinitionsAll.filter(pd => !pd.flags?.includes('system_managed')).map(pd => {
            const name = typeof pd.name === 'object' && pd.name !== null
                ? (pd.name as Record<string, string>).en || (pd.name as Record<string, string>).fr || Object.values(pd.name)[0] || pd.code || pd.uid
                : pd.name || pd.code || pd.uid
            const flags = []
            if (pd.data_type === 'enum' && pd.properties?.multiple === true) {
                flags.push('Multiple')
            }
            if ((pd.data_type === 'boolean' || pd.data_type === 'enum') && pd.flags?.includes('attribute')) {
                flags.push('Attribute')
            }
            const flagsStr = flags.length > 0 ? ` [${flags.join(', ')}]` : ''
            return {
                value: pd.uid || '',
                label: `${name} (${pd.data_type})${flagsStr}`,
                data: { code: pd.code, data_type: pd.data_type, uid: pd.uid }
            }
        }),
        [propertyDefinitionsAll]
    )

    const searchPropertyDefinitions = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        try {
            const response = await request('/api/v1/organizations/{orgCode}/properties', 'GET', {
                params: {
                    path: { orgCode: org },
                    query: { q: query, limit: 50 }
                }
            })
            const searchResults = ((response as { data?: PropertyDefinition[] })?.data || [])
            return searchResults.map((propDef: PropertyDefinition) => {
                const name = typeof propDef.name === 'object' && propDef.name !== null
                    ? (propDef.name as Record<string, string>).en || (propDef.name as Record<string, string>).fr || Object.values(propDef.name)[0] || propDef.code || propDef.uid
                    : propDef.name || propDef.code || propDef.uid
                const flags = []
                if (propDef.data_type === 'enum' && propDef.properties?.multiple === true) {
                    flags.push('Multiple')
                }
                if ((propDef.data_type === 'boolean' || propDef.data_type === 'enum') && propDef.flags?.includes('attribute')) {
                    flags.push('Attribute')
                }
                const flagsStr = flags.length > 0 ? ` [${flags.join(', ')}]` : ''
                return {
                    value: propDef.uid || '',
                    label: `${name} (${propDef.data_type})${flagsStr}`,
                    data: { code: propDef.code, data_type: propDef.data_type, uid: propDef.uid }
                }
            })
        } catch (error) {
            console.error('Error searching property definitions:', error)
            const filtered = initialOptions.filter(option =>
                option.label.toLowerCase().includes(query.toLowerCase())
            )
            return filtered.slice(0, 50)
        }
    }, [initialOptions, org, request])

    return { searchPropertyDefinitions, initialOptions, propertyDefinitionsAll }
}
